import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Heading, Text, VStack, Button, Code, Alert, AlertIcon, AlertDescription, HStack, Icon, Collapse, Link, UnorderedList, ListItem } from '@chakra-ui/react';
import { AlertTriangle, Copy, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

type WebGLErrorBoundaryProps = {
	children: ReactNode;
};

type WebGLErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
	isWebGLError: boolean;
	showTroubleshooting: boolean;
};

/**
 * Error boundary that catches and handles WebGL initialization errors.
 * Displays a user-friendly message with instructions to enable hardware acceleration.
 */
export class WebGLErrorBoundary extends Component<WebGLErrorBoundaryProps, WebGLErrorBoundaryState> {
	constructor(props: WebGLErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			isWebGLError: false,
			showTroubleshooting: false
		};
	}

	static getDerivedStateFromError(error: Error): Partial<WebGLErrorBoundaryState> {
		// Check if this is a WebGL-related error
		const isWebGLError = error.message?.toLowerCase().includes('webgl') ||
			error.message?.toLowerCase().includes('hardware acceleration') ||
			error.message?.toLowerCase().includes('graphics');

		return {
			hasError: true,
			error,
			isWebGLError
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log error details for debugging
		console.error('WebGLErrorBoundary caught error:', error, errorInfo);
	}

	private copySettingsLink = async (): Promise<void> => {
		const settingsUrl = 'chrome://settings/?search=Use%20graphics%20acceleration%20when%20available';
		try {
			await navigator.clipboard.writeText(settingsUrl);
		} catch (err) {
			console.error('Failed to copy settings link:', err);
		}
	};

	private reload = (): void => {
		window.location.reload();
	};

	private toggleTroubleshooting = (): void => {
		this.setState(prevState => ({
			showTroubleshooting: !prevState.showTroubleshooting
		}));
	};

	render(): ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		// If it's a WebGL error, show the helpful message
		if (this.state.isWebGLError) {
			const chromeSettingsUrl = 'chrome://settings/?search=Use%20graphics%20acceleration%20when%20available';
			const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

			return (
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					w="100%"
					h="100%"
					p={8}
					bgColor="gray.50"
				>
					<VStack spacing={6} maxW="600px" align="stretch">
						<HStack spacing={3} color="orange.500">
							<Icon as={AlertTriangle} boxSize={8} />
							<Heading size="lg" color="gray.800">3D View Unavailable</Heading>
						</HStack>

						<Alert status="warning" borderRadius="md">
							<AlertIcon />
							<AlertDescription>
								The 3D view requires hardware acceleration to be enabled in your browser.
							</AlertDescription>
						</Alert>

						<VStack spacing={4} align="stretch" bg="white" p={6} borderRadius="md" boxShadow="sm">
							<Text fontSize="md" fontWeight="semibold" color="gray.700">
								To enable hardware acceleration:
							</Text>

							{isChrome ? (
								<>
									<VStack spacing={2} align="stretch">
										<Text fontSize="sm" color="gray.600">
											1. Copy and paste this URL into a new Chrome tab:
										</Text>
										<HStack spacing={2}>
											<Code p={2} borderRadius="md" flex={1} fontSize="xs" overflowX="auto">
												{chromeSettingsUrl}
											</Code>
											<Button
												size="sm"
												leftIcon={<Copy size={16} />}
												onClick={this.copySettingsLink}
												colorScheme="blue"
												variant="outline"
											>
												Copy
											</Button>
										</HStack>
										<Text fontSize="sm" color="gray.600">
											2. Toggle on "Use graphics acceleration when available"
										</Text>
										<Text fontSize="sm" color="gray.600">
											3. Click "Relaunch" to restart Chrome
										</Text>
									</VStack>
								</>
							) : (
								<VStack spacing={2} align="stretch">
									<Text fontSize="sm" color="gray.600">
										1. Open your browser settings
									</Text>
									<Text fontSize="sm" color="gray.600">
										2. Search for "hardware acceleration" or "graphics acceleration"
									</Text>
									<Text fontSize="sm" color="gray.600">
										3. Enable the setting
									</Text>
									<Text fontSize="sm" color="gray.600">
										4. Restart your browser
									</Text>
								</VStack>
							)}

							{isChrome && (
								<Alert status="info" borderRadius="md" fontSize="sm">
									<AlertIcon />
									<AlertDescription>
										Chrome will need to restart for changes to take effect.
									</AlertDescription>
								</Alert>
							)}
						</VStack>

						<VStack spacing={3} align="stretch">
							<Button
								colorScheme="blue"
								onClick={this.reload}
								size="lg"
							>
								Reload Page
							</Button>

							<Button
								variant="outline"
								size="sm"
								leftIcon={<ExternalLink size={16} />}
								as="a"
								href="https://www.chromium.org/developers/design-documents/gpu-accelerated-compositing-in-chrome/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more about hardware acceleration
							</Button>
						</VStack>

						<VStack spacing={2} align="stretch" bg="white" borderRadius="md" boxShadow="sm">
							<Button
								onClick={this.toggleTroubleshooting}
								variant="ghost"
								width="100%"
								justifyContent="space-between"
								rightIcon={<Icon as={this.state.showTroubleshooting ? ChevronUp : ChevronDown} />}
								fontWeight="semibold"
								color="gray.700"
								_hover={{ bg: 'gray.50' }}
							>
								Additional Troubleshooting Steps
							</Button>

							<Collapse in={this.state.showTroubleshooting} animateOpacity>
								<Box
									maxH="300px"
									overflowY="auto"
									px={6}
									pb={6}
									css={{
										'&::-webkit-scrollbar': {
											width: '8px',
										},
										'&::-webkit-scrollbar-track': {
											background: '#f1f1f1',
											borderRadius: '4px',
										},
										'&::-webkit-scrollbar-thumb': {
											background: '#888',
											borderRadius: '4px',
										},
										'&::-webkit-scrollbar-thumb:hover': {
											background: '#555',
										},
									}}
								>
									<VStack spacing={5} align="stretch">
										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												1. Check WebGL Support
											</Heading>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>
													Visit <Code fontSize="xs">chrome://gpu/</Code> in your browser to check graphics feature status
												</ListItem>
												<ListItem>
													Look for "WebGL" and "WebGL2" - they should show "Hardware accelerated"
												</ListItem>
												<ListItem>
													If they show "Software only" or "Disabled", continue with steps below
												</ListItem>
											</UnorderedList>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												2. Update Graphics Drivers
											</Heading>
											<VStack spacing={3} align="stretch">
												<Box>
													<Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
														Windows:
													</Text>
													<UnorderedList spacing={1} fontSize="sm" color="gray.600">
														<ListItem>Open "Device Manager" (search in Windows taskbar)</ListItem>
														<ListItem>Expand "Display adapters"</ListItem>
														<ListItem>Right-click your graphics card and select "Update driver"</ListItem>
														<ListItem>Choose "Search automatically for drivers"</ListItem>
													</UnorderedList>
												</Box>

												<Box>
													<Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
														For NVIDIA GPUs:
													</Text>
													<UnorderedList spacing={1} fontSize="sm" color="gray.600">
														<ListItem>
															Download the NVIDIA App:{' '}
															<Link
																href="https://www.nvidia.com/en-us/software/nvidia-app/"
																color="blue.600"
																isExternal
															>
																nvidia.com/nvidia-app
															</Link>
														</ListItem>
														<ListItem>Check for driver updates in the Drivers section</ListItem>
														<ListItem>Try switching between "Studio" and "Game Ready" drivers</ListItem>
													</UnorderedList>
												</Box>

												<Box>
													<Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
														For AMD GPUs:
													</Text>
													<Text fontSize="sm" color="gray.600">
														Visit AMD's driver download page and install latest drivers
													</Text>
												</Box>
											</VStack>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												3. Force Enable WebGL in Chrome
											</Heading>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>Go to <Code fontSize="xs">chrome://flags</Code></ListItem>
												<ListItem>Search for "WebGL"</ListItem>
												<ListItem>Set "WebGL Developer Extensions" to Enabled</ListItem>
												<ListItem>Set "Override software rendering list" to Enabled</ListItem>
												<ListItem>Click "Relaunch" at the bottom</ListItem>
											</UnorderedList>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												4. Set Chrome to Use Dedicated GPU (Windows 11)
											</Heading>
											<Text fontSize="sm" color="gray.600" mb={2}>
												If you have both integrated and dedicated graphics:
											</Text>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>Open Settings &gt; System &gt; Display</ListItem>
												<ListItem>Scroll down and click "Graphics"</ListItem>
												<ListItem>
													Find Google Chrome (or click Browse and navigate to{' '}
													<Code fontSize="xs">C:\Program Files\Google\Chrome\Application\chrome.exe</Code>)
												</ListItem>
												<ListItem>Click Chrome, then "Options"</ListItem>
												<ListItem>Select "High performance" (should show your dedicated GPU)</ListItem>
												<ListItem>Click Save and restart Chrome completely</ListItem>
											</UnorderedList>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												5. Check for Conflicting Extensions
											</Heading>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>Go to <Code fontSize="xs">chrome://extensions/</Code></ListItem>
												<ListItem>Try disabling extensions one by one</ListItem>
												<ListItem>Test the 3D viewer after each disable</ListItem>
												<ListItem>Re-enable extensions that aren't causing issues</ListItem>
											</UnorderedList>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												6. Update Chrome
											</Heading>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>Go to <Code fontSize="xs">chrome://settings/help</Code></ListItem>
												<ListItem>Chrome will check for and install updates</ListItem>
												<ListItem>Restart the browser after updating</ListItem>
											</UnorderedList>
										</Box>

										<Box>
											<Heading size="sm" color="gray.700" mb={2}>
												7. Clear Browser Data & Restart
											</Heading>
											<UnorderedList spacing={1} fontSize="sm" color="gray.600">
												<ListItem>Go to <Code fontSize="xs">chrome://settings/clearBrowserData</Code></ListItem>
												<ListItem>Select "Cached images and files"</ListItem>
												<ListItem>Click "Clear data"</ListItem>
												<ListItem>
													Completely close Chrome (check Task Manager to ensure all Chrome processes are closed)
												</ListItem>
												<ListItem>Reopen Chrome and try again</ListItem>
											</UnorderedList>
										</Box>
									</VStack>
								</Box>
							</Collapse>
						</VStack>

						<Box pt={4} borderTop="1px solid" borderColor="gray.200">
							<Text fontSize="xs" color="gray.500" fontStyle="italic">
								Error: {this.state.error?.message}
							</Text>
						</Box>
					</VStack>
				</Box>
			);
		}

		// For non-WebGL errors, show a generic error message
		return (
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				w="100%"
				h="100%"
				p={8}
				bgColor="gray.50"
			>
				<VStack spacing={4} maxW="500px">
					<HStack spacing={3} color="red.500">
						<Icon as={AlertTriangle} boxSize={8} />
						<Heading size="lg" color="gray.800">Something went wrong</Heading>
					</HStack>
					<Text color="gray.600">
						An unexpected error occurred while loading the 3D view.
					</Text>
					<Button colorScheme="blue" onClick={this.reload}>
						Reload Page
					</Button>
					{import.meta.env.DEV && (
						<Box pt={4} borderTop="1px solid" borderColor="gray.200" w="100%">
							<Code p={3} borderRadius="md" fontSize="xs" display="block" whiteSpace="pre-wrap">
								{this.state.error?.message}
							</Code>
						</Box>
					)}
				</VStack>
			</Box>
		);
	}
}
